import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { preservedHtmlExtensions } from './extensions/PreservedHtml'
import type { HtmlPreviewRoleResolver } from './extensions/PreservedHtml'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import { useCallback, useEffect, useImperativeHandle, forwardRef, useRef } from 'react'
import { TemplateVariableNode } from './extensions/TemplateVariableNode'
import { FontSize } from './extensions/FontSize'
import { BackgroundColor } from './extensions/BackgroundColor'
import { TiptapToolbar } from './TiptapToolbar'
import { EmailPreviewFrame } from './EmailPreviewFrame'
import { useFitToWidth } from './useFitToWidth'
import './tiptap.css'

interface TiptapEditorProps {
    content: string
    onChange: (html: string) => void
    placeholder?: string
    editable?: boolean
    onFocus?: () => void
    minHeight?: number
    className?: string
    // Preview toggle wiring. When `onTogglePreview` is provided, the toolbar
    // renders an eye icon; when `isPreviewActive` is true, `previewHtml` is
    // rendered in a sandboxed iframe in place of the editor surface.
    onTogglePreview?: () => void
    isPreviewActive?: boolean
    previewHtml?: string
    // Suppresses the internal toolbar so a parent can render it somewhere else
    // (e.g. the signature onboarding card hoists the toolbar above a logo-tile
    // + editor row so it spans the full card width).
    hideToolbar?: boolean
    /** Keep hand-authored HTML (nested divs + inline styles) through the round-trip. Off by default —
     *  see extensions/PreservedHtml. */
    preserveHtml?: boolean
    /** Optional feature-owned classifier for editor-only semantic preview roles. */
    htmlPreviewRoleResolver?: HtmlPreviewRoleResolver
    /** Shrink content wider than the editor so it fits, never enlarging it. A designed email is laid out
     *  600–700px wide; in a narrow pane it would run off the side. Only the screen is scaled — the HTML is not. */
    fitToWidth?: boolean
    // Emits the ProseMirror editor instance as it becomes available so a
    // parent can drive an externally-rendered toolbar.
    onEditorReady?: (editor: Editor | null) => void
}

const PREVIEW_BODY_STYLE = 'margin:0;padding:8px 12px;font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;'

export interface TiptapEditorRef {
    editor: Editor | null
    insertVariable: (variableName: string) => void
}

const getExtensions = (placeholder?: string, preserveHtml?: boolean, htmlPreviewRoleResolver?: HtmlPreviewRoleResolver) => {
    return [
        StarterKit.configure({
            heading: false,
            codeBlock: false,
            code: false,
            blockquote: false,
            horizontalRule: false,
        }),
        Underline,
        Link.configure({
            openOnClick: false,
            HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        }),
        Placeholder.configure({ placeholder: placeholder || '' }),
        TextStyle,
        Color,
        FontSize,
        BackgroundColor,
        TemplateVariableNode,
        // Opt-in: only callers that seed hand-authored HTML need these, and widening the default schema
        // would change how every other consumer's content round-trips.
        ...(preserveHtml ? preservedHtmlExtensions(htmlPreviewRoleResolver) : []),
    ]
}

export const TiptapEditor = forwardRef<TiptapEditorRef, TiptapEditorProps>(
    (
        {
            content,
            onChange,
            placeholder,
            editable = true,
            onFocus,
            minHeight,
            className,
            onTogglePreview,
            isPreviewActive = false,
            previewHtml,
            hideToolbar = false,
            onEditorReady,
            preserveHtml = false,
            htmlPreviewRoleResolver,
            fitToWidth = false,
        },
        ref
    ) => {
        const isUpdatingRef = useRef(false)

        const editor = useEditor({
            extensions: getExtensions(placeholder, preserveHtml, htmlPreviewRoleResolver),
            content,
            editable,
            onUpdate: ({ editor }) => {
                if (isUpdatingRef.current) return
                onChange(editor.getHTML())
            },
            onFocus: () => onFocus?.(),
        })

        useEffect(() => {
            if (editor && content !== editor.getHTML()) {
                isUpdatingRef.current = true
                editor.commands.setContent(content, false)
                isUpdatingRef.current = false
            }
        }, [content, editor])

        useEffect(() => {
            if (editor) {
                editor.setEditable(editable)
            }
        }, [editable, editor])

        useFitToWidth(editor, fitToWidth)

        const insertVariable = useCallback(
            (variableName: string) => {
                if (!editor) return
                editor.chain().focus().insertContent({ type: 'templateVariable', attrs: { variableName } }).run()
            },
            [editor]
        )

        // The ProseMirror node is only as tall as its content while the padded container stretches to
        // minHeight — a click on the "empty" area below the text (or in the padding) landed on dead space
        // and the caret went nowhere. Map any such click to the nearest document position so the editor
        // behaves like a textarea: click anywhere, the caret lands.
        const handleContainerMouseDown = useCallback(
            (e: React.MouseEvent) => {
                if (!editor || !editable) return
                const target = e.target as HTMLElement
                if (target.closest('.ProseMirror')) return // a real editor click — ProseMirror handles it
                e.preventDefault()
                const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY })
                if (pos) editor.chain().focus().setTextSelection(pos.pos).run()
                else editor.commands.focus('end')
            },
            [editor, editable]
        )

        useImperativeHandle(ref, () => ({ editor, insertVariable }), [editor, insertVariable])

        useEffect(() => {
            onEditorReady?.(editor)
        }, [editor, onEditorReady])

        return (
            <div className={`tiptap-editor-wrapper ${className || ''}`}>
                {!hideToolbar && <TiptapToolbar editor={editor} onTogglePreview={onTogglePreview} isPreviewActive={isPreviewActive} />}
                {isPreviewActive ? (
                    <EmailPreviewFrame
                        title="Email content preview"
                        html={previewHtml || ''}
                        bodyStyle={PREVIEW_BODY_STYLE}
                        className="tiptap-content tiptap-preview"
                        style={minHeight ? { minHeight } : undefined}
                    />
                ) : (
                    <EditorContent
                        editor={editor}
                        className="tiptap-content"
                        style={minHeight ? { minHeight } : undefined}
                        onMouseDown={handleContainerMouseDown}
                    />
                )}
            </div>
        )
    }
)

TiptapEditor.displayName = 'TiptapEditor'
